import React, { Component, createRef } from "react";
import "./MfaCodeVerification.css";

/**
 * Componente de verificação do código MFA
 * Exibe o formulário para inserir o código enviado por e-mail
 *
 * @param {string} email - E-mail do usuário (para exibição)
 * @param {function} onVerify - Callback executado ao verificar o código
 * @param {function} onResendCode - Callback executado ao reenviar o código
 */
class MfaCodeVerification extends Component {
  constructor(props) {
    super(props);

    this.state = {
      digits: ["", "", "", "", "", ""],
      timeLeft: 59,
      canResend: false,
      hasError: false,
      validated: false,
    };

    // Criar refs para cada input
    this.inputRefs = Array(6).fill(null).map(() => createRef());

    this.timerInterval = null;
    this.handleDigitChange = this.handleDigitChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleResendCode = this.handleResendCode.bind(this);
  }

  componentDidMount() {
    this.startTimer();
    this.focusFirstInput();
  }

  focusFirstInput() {
    // Tenta focar múltiplas vezes para garantir que funcione após o modal fechar
    const attemptFocus = (attempts = 0) => {
      if (attempts > 5) return;

      setTimeout(() => {
        if (this.inputRefs[0] && this.inputRefs[0].current) {
          this.inputRefs[0].current.focus();
          // Verifica se realmente focou, senão tenta novamente
          if (document.activeElement !== this.inputRefs[0].current) {
            attemptFocus(attempts + 1);
          }
        }
      }, 150 * (attempts + 1));
    };

    attemptFocus();
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  startTimer() {
    this.setState({ timeLeft: 59, canResend: false });

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      this.setState(prevState => {
        if (prevState.timeLeft <= 1) {
          clearInterval(this.timerInterval);
          return { timeLeft: 0, canResend: true };
        }
        return { timeLeft: prevState.timeLeft - 1 };
      });
    }, 1000);
  }

  handleDigitChange(index, e) {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length <= 1) {
      const newDigits = [...this.state.digits];
      newDigits[index] = value;
      this.setState({ digits: newDigits, hasError: false }, () => {
        // Se digitou um número, move para o próximo input
        if (value && index < 5) {
          this.inputRefs[index + 1].current.focus();
        }

        // Verifica se completou todos os dígitos
        const code = newDigits.join("");
        if (code.length === 6) {
          this.validateCode(code);
        }
      });
    }
  }

  validateCode(code) {
    const { onVerify } = this.props;
    this.setState({ hasError: false });
    if (onVerify) {
      onVerify(code);
    }
  }

  setError(hasError) {
    this.setState({ hasError, validated: false });
  }

  setValidated(validated) {
    this.setState({ validated, hasError: false });
  }

  handleKeyDown(index, e) {
    // Backspace - volta para o input anterior
    if (e.key === "Backspace") {
      if (!this.state.digits[index] && index > 0) {
        this.inputRefs[index - 1].current.focus();
      }
    }

    // Arrow Left - volta para o input anterior
    if (e.key === "ArrowLeft" && index > 0) {
      this.inputRefs[index - 1].current.focus();
    }

    // Arrow Right - avança para o próximo input
    if (e.key === "ArrowRight" && index < 5) {
      this.inputRefs[index + 1].current.focus();
    }
  }

  handlePaste(e) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);

    if (pastedData) {
      const newDigits = [...this.state.digits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      this.setState({ digits: newDigits, hasError: false }, () => {
        // Foca no último input preenchido ou no próximo vazio
        const lastFilledIndex = Math.min(pastedData.length - 1, 5);
        this.inputRefs[lastFilledIndex].current.focus();

        // Verifica se completou todos os dígitos
        const code = newDigits.join("");
        if (code.length === 6) {
          this.validateCode(code);
        }
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    const code = this.state.digits.join("");
    const { onVerify } = this.props;

    if (code.length === 6 && onVerify) {
      onVerify(code);
    }
  }

  handleResendCode() {
    const { onResendCode } = this.props;

    // Limpa os inputs
    this.setState({ digits: ["", "", "", "", "", ""] }, () => {
      if (this.inputRefs[0].current) {
        this.inputRefs[0].current.focus();
      }
    });

    if (onResendCode) {
      onResendCode();
    }

    this.startTimer();
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  render() {
    const { email } = this.props;
    const { digits, timeLeft, canResend, hasError, validated } = this.state;

    return (
      <div className="mfa-verification">
        <h3 className="mfa-verification__title">Conecte-se</h3>

        <div className="mfa-verification__header">
          <h4 className="mfa-verification__subtitle">
            Insira o código que enviamos<br />
            para o seu endereço de e-mail
          </h4>
          <p className="mfa-verification__email-info">
            Enviamos o código para <strong>{email}</strong>.
          </p>
        </div>

        <form className="mfa-verification__form" onSubmit={this.handleSubmit}>
          <div className="mfa-verification__code-inputs">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={this.inputRefs[index]}
                type="text"
                inputMode="numeric"
                className={`mfa-verification__digit-input ${hasError ? "mfa-verification__digit-input--error" : ""}`}
                value={digit}
                onChange={(e) => this.handleDigitChange(index, e)}
                onKeyDown={(e) => this.handleKeyDown(index, e)}
                onPaste={this.handlePaste}
                maxLength={1}
                autoComplete="off"
                autoFocus={index === 0}
              />
            ))}
          </div>

          <div className="mfa-verification__error-container">
            <span className={`mfa-verification__error-message ${hasError ? "mfa-verification__error-message--visible" : ""}`}>
              Código inválido. Verifique e tente novamente.
            </span>
            <span className={`mfa-verification__success-message ${validated ? "mfa-verification__success-message--visible" : ""}`}>
              Código validado
            </span>
          </div>

          <button
            type="button"
            className={`mfa-verification__resend-btn ${!canResend ? "mfa-verification__resend-btn--disabled" : ""}`}
            onClick={this.handleResendCode}
            disabled={!canResend}
          >
            Reenviar código
          </button>

          <div className="mfa-verification__timer">
            {this.formatTime(timeLeft)}
          </div>

        </form>
      </div>
    );
  }
}

export default MfaCodeVerification;

